import type { SvgIconProps } from "@mui/material";
import type { ComponentType } from "react";

import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";

export type ScoutSuggestion = {
  /** Short label shown in the disclosure row. */
  label: string;
  /** The full prompt sent to Scout when the row is picked. */
  prompt: string;
};

export type ScoutSuggestionCategory = {
  key: string;
  /** Short label for the category chip (kept brief so chips fit on one line). */
  label: string;
  Icon: ComponentType<SvgIconProps>;
  items: ScoutSuggestion[];
};

/**
 * Progressive-disclosure starter prompts for the Scout new-chat screen.
 *
 * These are framed as PROGRAM-level / portfolio-wide questions, not single-
 * facility ones: the goal is to demonstrate access to the customer's data and
 * produce a quick program overview (counts, breakdowns, roll-ups) that holds up
 * across thousands of facilities, waste streams, and chemicals. The Documents
 * prompts are written so Scout first explains the document-analysis workflow and
 * invites an upload, rather than assuming a document is already attached.
 */
export const SCOUT_SUGGESTION_CATEGORIES: ScoutSuggestionCategory[] = [
  {
    key: "agencies",
    label: "Agencies",
    Icon: AccountBalanceOutlined,
    items: [
      {
        label: "Map our regulatory footprint",
        prompt:
          "Give me a program-level overview of our regulatory footprint: how many facilities we have, across how many states, and which reporting programs (EPCRA, RCRA, and others) they fall under.",
      },
      {
        label: "How requirements differ by state",
        prompt:
          "Summarize how our reporting obligations and deadlines differ across the states where we operate.",
      },
      {
        label: "Where requirements are heaviest",
        prompt:
          "Which states and jurisdictions place the most reporting requirements on us, and how many of our facilities are affected?",
      },
      {
        label: "New York vs. NYC",
        prompt:
          "How do reporting thresholds and requirements differ between New York State and New York City?",
      },
    ],
  },
  {
    key: "chemicals",
    label: "Chemicals",
    Icon: ScienceOutlined,
    items: [
      {
        label: "Program overview",
        prompt:
          "Give me a program-wide overview of our Tier II chemical reporting. If we're currently in Tier II reporting season (before March 1), summarize our filing status across the portfolio — how many facilities are filed, in progress, and not started. If the season is over (after March 1), summarize the last season instead: reports filed by state and filing fees by state.",
      },
      {
        label: "Facilities over threshold",
        prompt:
          "How many of our facilities have chemicals over Tier II reporting thresholds, and which states are they concentrated in?",
      },
      {
        label: "Extra state data requirements",
        prompt:
          "Which states require additional Tier II data from us, and how many of our facilities does that affect?",
      },
    ],
  },
  {
    key: "hazardous-waste",
    label: "Waste",
    Icon: DeleteOutline,
    items: [
      {
        label: "Generator status breakdown",
        prompt:
          "Give me a portfolio-wide breakdown of our hazardous waste generator status (VSQG, SQG, LQG) across all facilities.",
      },
      {
        label: "Largest waste streams",
        prompt:
          "Across all of our facilities, what are our largest hazardous waste streams by volume?",
      },
      {
        label: "Reporting calendar",
        prompt:
          "What annual and biennial hazardous waste reports are coming due across our facilities, and when?",
      },
    ],
  },
  {
    key: "documents",
    label: "Documents",
    Icon: DescriptionOutlined,
    items: [
      {
        label: "Analyze a permit",
        prompt:
          "I want to analyze a permit. Before I upload anything, walk me through what you can pull out of it and how to get started.",
      },
      {
        label: "Extract dates & obligations",
        prompt:
          "I'd like to extract the key dates, limits, and obligations from a compliance document. Explain how that works here and what you need from me, then I'll upload it.",
      },
      {
        label: "Summarize a long document",
        prompt:
          "I have a long permit or report I want summarized. Describe how document analysis works before I drag it in.",
      },
      {
        label: "Compare two versions",
        prompt:
          "I want to compare two versions of a permit. Tell me how you'd approach it and what to provide, then I'll add the files.",
      },
      {
        label: "Set up a document review",
        prompt:
          "Help me set up a review of a regulatory document — explain the steps first, then I'll upload it.",
      },
    ],
  },
];
